import { rateUnits } from '_legacy/modules/columns/rate/models/RateUnits';
import getDatasetSpecificHeadingsTitles from '../services/headingsServices/getDatasetSpecificHeadingsTitles';
import {
    SHORT_HAND_QUANTITY_FORMAT,
    LONG_HAND_QUANTITY_FORMAT,
} from '_legacy/modules/columns/quantity/services/QuatityServiceFactory';
import {
    TCT,
    VOY,
    PERIOD,
} from '_legacy/components/toolPanels/commonSettings/OrderOrFixtureTypes';
import {
    defaultRateUnitRanges,
    lpgRateUnitRanges,
    resolveRateUnit,
    resolveRateUnitForTankers,
} from './RateUnitRanges';
import { TankersGroupType } from './TankersGroupTypes';
import {
    DAYS,
    LEGS_LABEL,
    LEGS_VALUE,
    MONTHS,
    PNR,
    VOYS,
    YEARS,
} from '_legacy/modules/shared/columns/models/DurationUnits';

interface IDataset {
    label: string;
    value: number;
    allowFreeTextCargo: boolean;
    allowedPlaceholderCargoes: string[];
    allowFreeTextZone: boolean;
    allowFreeTextLocation: boolean;
    allowSearchCountries: boolean;
    searchVesselsWithImoOnly: boolean;
    nonAvailableFields: string[];
    nonAvailableOrderFields?: string[];
    defaultOrderColumnsToShow: string[];
    defaultFixtureColumnsToShow: string[];
    defaultQuantityFormat: string;
    defaultType?: string | ((tankersGroupType?: number | null) => string);
    isStemSizeAvailable: boolean;
    autoPopulateDirectionEnabled: boolean;
    shouldHighlightNewOrders: boolean;
    shouldShowConvertedOrders: boolean;
    shouldResetCargoIfPeriodType: boolean;
    maxLocationsSearchResult: number;
    vesselOptions: {
        minCapacity: number;
        maxCapacity: number;
    };
    numberForQuantityFormating: number;
    headingsMapping: {
        areas: string;
    };
    allowSearchVesselsByEniNumber: boolean;
    nonAvailableDurationUnits: string[];
    customDurationOrder?: string[];
    resolveRateUnit?: (
        amountFrom: number,
        fixtureType?: string,
        tankersGroupType?: any
    ) => any;
}

export const DATASET_SUEZMAX_LONDON = 1;
export const DATASET_VLCC_HOUSTON = 2;
export const DATASET_AFRAMAX_LONDON = 3;
export const DATASET_CLEAN_HOUSTON = 4;
export const DATASET_SUEZMAX_HOUSTON = 5;
export const DATASET_VLCC_LONDON = 6;
export const DATASET_AFRAMAX_HOUSTON = 7;
export const DATASET_CLEAN_LONDON = 8;
export const DATASET_DIRTY_HOUSTON_25_85 = 9;
export const DATASET_FUEL_OIL_LONDON = 10;
export const DATASET_DIRTY_DUBAI_25_130 = 11;
export const DATASET_SHELL = 12;
export const DATASET_SPECIALISED = 13;
export const DATASET_DRY_CARGO = 14;
export const DATASET_TANKERS = 15;
export const DATASET_PCG = 16;
export const DATASET_LPG_VLGC = 17;
export const DATASET_LPG_MIDSIZE = 18;
export const DATASET_LNG = 19;

export const DATASET_TEST = 99;

export const gasDatasetIds = [
    DATASET_PCG,
    DATASET_LPG_VLGC,
    DATASET_LPG_MIDSIZE,
    DATASET_LNG,
];

export const nonTankersDatasetIds = [
    DATASET_SPECIALISED,
    DATASET_DRY_CARGO,
    DATASET_PCG,
    DATASET_LPG_VLGC,
    DATASET_LPG_MIDSIZE,
    DATASET_LNG,
];

export const datasetIdsWithoutIndividualVisibilityOption = [
    DATASET_TANKERS,
    ...gasDatasetIds,
];

export const tankersUnstructuredDatasetIds = [
    DATASET_SUEZMAX_LONDON,
    DATASET_VLCC_HOUSTON,
    DATASET_AFRAMAX_LONDON,
    DATASET_CLEAN_HOUSTON,
    DATASET_SUEZMAX_HOUSTON,
    DATASET_VLCC_LONDON,
    DATASET_AFRAMAX_HOUSTON,
    DATASET_CLEAN_LONDON,
    DATASET_DIRTY_HOUSTON_25_85,
    DATASET_FUEL_OIL_LONDON,
    DATASET_DIRTY_DUBAI_25_130,
    DATASET_SHELL,
];

export const dataSets: IDataset[] = [
    {
        label: 'SUEZMAX LONDON',
        value: DATASET_SUEZMAX_LONDON,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(
            DATASET_SUEZMAX_LONDON
        ),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'SUEZMAX HOUSTON',
        value: DATASET_SUEZMAX_HOUSTON,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(
            DATASET_SUEZMAX_HOUSTON
        ),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'VLCC LONDON',
        value: DATASET_VLCC_LONDON,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_VLCC_LONDON),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'VLCC HOUSTON',
        value: DATASET_VLCC_HOUSTON,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_VLCC_HOUSTON),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'AFRAMAX LONDON ',
        value: DATASET_AFRAMAX_LONDON,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(
            DATASET_AFRAMAX_LONDON
        ),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'AFRAMAX HOUSTON ',
        value: DATASET_AFRAMAX_HOUSTON,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(
            DATASET_AFRAMAX_HOUSTON
        ),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'CLEAN HOUSTON',
        value: DATASET_CLEAN_HOUSTON,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(
            DATASET_CLEAN_HOUSTON
        ),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'CLEAN LONDON',
        value: DATASET_CLEAN_LONDON,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_CLEAN_LONDON),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: '25-85 DTY HOU',
        value: DATASET_DIRTY_HOUSTON_25_85,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(
            DATASET_DIRTY_HOUSTON_25_85
        ),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'FUEL OIL LONDON',
        value: DATASET_FUEL_OIL_LONDON,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(
            DATASET_FUEL_OIL_LONDON
        ),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: '25-130 DIRTY DUBAI',
        value: DATASET_DIRTY_DUBAI_25_130,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(
            DATASET_DIRTY_DUBAI_25_130
        ),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'SHELL',
        value: DATASET_SHELL,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: ['PC'],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'charterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'brokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rate',
            'charterer',
            'demurrage',
            'brokers',
            'loadZone',
            'dischargeZone',
            'owner',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_SHELL),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'SPECIALISED',
        value: DATASET_SPECIALISED,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: [],
        allowFreeTextZone: false,
        allowFreeTextLocation: true,
        allowSearchCountries: true,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rate',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
        ],
        nonAvailableOrderFields: ['vesselCategory'],
        resolveRateUnit: (amountFrom) =>
            amountFrom >= 1000 ? rateUnits[0] : rateUnits[1],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'charterer',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'laycan',
            'brokers',
            'parcels',
            'comments',
            'privateComments',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'updatedDateTime',
            'reportedDate',
            'status',
            'type',
            'charterer',
            'vessel',
            'dwt',
            'owner',
            'cargoQuantity',
            'cargoParts',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'laycan',
            'rates',
            'freightPortBasis',
            'brokers',
            'clarksonsBrokers',
            'demurrage',
            'parcels',
            'comments',
            'privateComments',
        ],
        defaultQuantityFormat: LONG_HAND_QUANTITY_FORMAT,
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: false,
        maxLocationsSearchResult: 30,
        vesselOptions: { minCapacity: 0, maxCapacity: 60 },
        numberForQuantityFormating: 100,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_SPECIALISED),
        allowSearchVesselsByEniNumber: true,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'DRY CARGO',
        value: DATASET_DRY_CARGO,
        allowFreeTextCargo: false,
        allowFreeTextZone: false,
        allowFreeTextLocation: false,
        allowSearchCountries: true,
        searchVesselsWithImoOnly: true,
        allowedPlaceholderCargoes: ['2LL', 'LLEGS', 'PERIOD'],
        nonAvailableFields: [
            'rate',
            'charterer',
            'brokers',
            'owner',
            'eniNumber',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'week',
            'gasType',
            'tankTypeCode',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
        ],
        resolveRateUnit: (value) =>
            resolveRateUnit(value, defaultRateUnitRanges),
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'type',
            'thirdPartyCharterer',
            'laycan',
            'loadLocationParts',
            'dischargeLocationParts',
            'cargoParts',
            'cargoQuantity',
            'direction',
            'comments',
            'commission',
            'loadZone',
            'areas',
            'thirdPartyBrokers',
            'orderStatus',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'vessel',
            'dwt',
            'yob',
            'deliveryLocationParts',
            'loadLocationParts',
            'laycan',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'cargoParts',
            'cargoQuantity',
            'rates',
            'tceParts',
            'status',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'comments',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: TCT,
        isStemSizeAvailable: true,
        autoPopulateDirectionEnabled: true,
        shouldHighlightNewOrders: true,
        shouldShowConvertedOrders: true,
        shouldResetCargoIfPeriodType: false,
        maxLocationsSearchResult: 30,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_DRY_CARGO),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: [VOYS],
    },
    {
        label: 'TANKERS',
        value: DATASET_TANKERS,
        allowFreeTextCargo: false,
        allowFreeTextZone: false,
        allowFreeTextLocation: false,
        allowSearchCountries: true,
        searchVesselsWithImoOnly: false,
        allowedPlaceholderCargoes: ['PC'],
        nonAvailableFields: [
            'rate',
            'charterer',
            'brokers',
            'owner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'gasCbm',
            'gasType',
            'tankTypeCode',
            'engineType',
            'propType',
        ],
        resolveRateUnit: (value, fixtureType, tankersGroupType) =>
            resolveRateUnitForTankers(
                value,
                fixtureType as string,
                tankersGroupType
            ),
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'thirdPartyCharterer',
            'type',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'freightPortBasis',
            'laycan',
            'comments',
            'offers',
            'areas',
            'thirdPartyBrokers',
            'quoted',
            'commission',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'updatedDateTime',
            'vessel',
            'status',
            'type',
            'cargoQuantity',
            'cargoParts',
            'cleanDirtyCargo',
            'deliveryLocationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'redeliveryTerm',
            'redeliveryLocationParts',
            'freightPortBasis',
            'laycan',
            'rates',
            'tceParts',
            'thirdPartyCharterer',
            'demurrage',
            'thirdPartyBrokers',
            'loadZone',
            'dischargeZone',
            'thirdPartyOwner',
            'ownerReLet',
            'comments',
            'vesselCategory',
            'commission',
        ],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        defaultType: (tankersGroupType) => {
            if (tankersGroupType === TankersGroupType.Period) {
                return PERIOD;
            }
            if (tankersGroupType === TankersGroupType.Spot) {
                return VOY;
            }
            // For Tankers Spot groups or undefined, default to VOY
            return VOY;
        },
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: true,
        shouldShowConvertedOrders: true,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 30,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_TANKERS),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
    {
        label: 'PCG',
        value: DATASET_PCG,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: [],
        allowFreeTextZone: false,
        allowFreeTextLocation: false,
        allowSearchCountries: true,
        searchVesselsWithImoOnly: true,
        defaultQuantityFormat: LONG_HAND_QUANTITY_FORMAT,
        nonAvailableFields: [
            'charterer',
            'brokers',
            'owner',
            'areasGeared',
            'eniNumber',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'rate',
            'direction',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'groupName',
            'reportedDate',
            'type',
            'thirdPartyCharterer',
            'laycan',
            'loadLocationParts',
            'dischargeLocationParts',
            'cargoParts',
            'cargoQuantity',
            'comments',
            'commission',
            'loadZone',
            'areas',
            'thirdPartyBrokers',
            'orderStatus',
        ],
        defaultFixtureColumnsToShow: [
            'vessel',
            'gasCbm',
            'vesselCategory',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'laycan',
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'reportedDate',
            'groupName',
            'status',
            'type',
            'comments',
            'gasType',
            'tankTypeCode',
        ],
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        resolveRateUnit: (value) =>
            resolveRateUnit(value, defaultRateUnitRanges),
        numberForQuantityFormating: 100,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_PCG),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: [],
        customDurationOrder: [DAYS, MONTHS, YEARS, VOYS, LEGS_VALUE, PNR],
    },
    {
        label: 'LPG - VLGC',
        value: DATASET_LPG_VLGC,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: [],
        allowFreeTextZone: false,
        allowFreeTextLocation: false,
        allowSearchCountries: true,
        searchVesselsWithImoOnly: false,
        defaultQuantityFormat: LONG_HAND_QUANTITY_FORMAT,
        nonAvailableFields: [
            'rate',
            'charterer',
            'brokers',
            'owner',
            'areasGeared',
            'eniNumber',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'direction',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'isSelected',
            'privacy',
            'groupName',
            'reportedDate',
            'type',
            'thirdPartyCharterer',
            'laycan',
            'loadLocationParts',
            'dischargeLocationParts',
            'cargoParts',
            'cargoQuantity',
            'comments',
            'commission',
            'loadZone',
            'areas',
            'thirdPartyBrokers',
            'orderStatus',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'privacy',
            'type',
            'vessel',
            'thirdPartyCharterer',
            'thirdPartyOwner',
            'laycan',
            'durationParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'rates',
            'thirdPartyBrokers',
            'comments',
            'cargoQuantity',
            'status',
            'cpDate',
            'groupName',
            'week',
        ],
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        resolveRateUnit: (value) => resolveRateUnit(value, lpgRateUnitRanges),
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_LPG_VLGC),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: [VOYS],
    },
    {
        label: 'LPG - MIDSIZE',
        value: DATASET_LPG_MIDSIZE,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: [],
        allowFreeTextZone: false,
        allowFreeTextLocation: false,
        allowSearchCountries: true,
        searchVesselsWithImoOnly: true,
        defaultQuantityFormat: LONG_HAND_QUANTITY_FORMAT,
        nonAvailableFields: [
            'charterer',
            'brokers',
            'owner',
            'areasGeared',
            'eniNumber',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'rate',
            'direction',
            'tceParts',
            'engineType',
            'propType',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'groupName',
            'reportedDate',
            'type',
            'thirdPartyCharterer',
            'laycan',
            'loadLocationParts',
            'dischargeLocationParts',
            'cargoParts',
            'cargoQuantity',
            'comments',
            'commission',
            'loadZone',
            'areas',
            'thirdPartyBrokers',
            'orderStatus',
        ],
        defaultFixtureColumnsToShow: [
            'isSelected',
            'type',
            'laycan',
            'vessel',
            'gasType',
            'tankTypeCode',
            'vesselCategory',
            'gasCbm',
            'thirdPartyOwner',
            'thirdPartyCharterer',
            'loadLocationParts',
            'dischargeLocationParts',
            'durationParts',
            'rates',
            'thirdPartyBrokers',
            'cargoParts',
            'comments',
            'status',
            'cpDate',
            'groupName',
            'week',
        ],
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        resolveRateUnit: (value) =>
            resolveRateUnit(value, defaultRateUnitRanges),
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_PCG),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: [VOYS],
    },
    {
        label: 'LNG',
        value: DATASET_LNG,
        allowFreeTextCargo: false,
        allowedPlaceholderCargoes: [],
        allowFreeTextZone: false,
        allowFreeTextLocation: false,
        allowSearchCountries: true,
        searchVesselsWithImoOnly: true,
        defaultQuantityFormat: LONG_HAND_QUANTITY_FORMAT,
        nonAvailableFields: [
            'charterer',
            'brokers',
            'owner',
            'areasGeared',
            'eniNumber',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'buyer',
            'opportunity',
            'operator',
            'rate',
            'direction',
            'tceParts',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [
            'groupName',
            'reportedDate',
            'type',
            'thirdPartyCharterer',
            'nearForward',
            'laycan',
            'loadLocationParts',
            'dischargeLocationParts',
            'cargoParts',
            'cargoQuantity',
            'comments',
            'commission',
            'loadZone',
            'areas',
            'thirdPartyBrokers',
            'orderStatus',
            'loadEW',
            'intraInterRegional',
            'dischEW',
        ],
        defaultFixtureColumnsToShow: [
            'vessel',
            'gasCbm',
            'vesselCategory',
            'cargoQuantity',
            'cargoParts',
            'loadLocationParts',
            'dischargeLocationParts',
            'nearForward',
            'laycan',
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'reportedDate',
            'groupName',
            'status',
            'type',
            'comments',
            'gasType',
            'tankTypeCode',
            'loadEW',
            'intraInterRegional',
            'dischEW',
        ],
        defaultType: VOY,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: true,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        resolveRateUnit: (value) =>
            resolveRateUnit(value, defaultRateUnitRanges),
        numberForQuantityFormating: 100,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_LNG),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: [VOYS],
    },
    {
        label: 'Test',
        value: DATASET_TEST,
        allowFreeTextCargo: true,
        allowedPlaceholderCargoes: [],
        allowFreeTextZone: true,
        allowFreeTextLocation: true,
        allowSearchCountries: false,
        searchVesselsWithImoOnly: false,
        nonAvailableFields: [
            'rates',
            'thirdPartyCharterer',
            'thirdPartyBrokers',
            'thirdPartyOwner',
            'direction',
            'areasGeared',
            'eniNumber',
            'orderStatus',
            'orderVessel',
            'orderThirdPartyOwner',
            'orderRates',
            'salePrice',
            'saleDate',
            'hireExpiry',
            'cleanDirtyCargo',
            'ownerReLet',
            'dualFuel',
        ],
        defaultOrderColumnsToShow: [],
        defaultFixtureColumnsToShow: [],
        defaultQuantityFormat: SHORT_HAND_QUANTITY_FORMAT,
        isStemSizeAvailable: false,
        autoPopulateDirectionEnabled: false,
        shouldHighlightNewOrders: false,
        shouldShowConvertedOrders: false,
        shouldResetCargoIfPeriodType: false,
        maxLocationsSearchResult: 15,
        vesselOptions: {
            minCapacity: 0,
            maxCapacity: 750,
        },
        numberForQuantityFormating: 1000,
        headingsMapping: getDatasetSpecificHeadingsTitles(DATASET_TEST),
        allowSearchVesselsByEniNumber: false,
        nonAvailableDurationUnits: ['LL', VOYS],
    },
];

export const getDataset = (id) =>
    dataSets.find((dataset) => dataset.value === id);

export const getDefaultTypeForGroup = (
    datasetId: number | null,
    tankersGroupType?: TankersGroupType | null
) => {
    const dataset = getDataset(datasetId);

    if (!dataset?.defaultType) {
        return VOY;
    }

    if (typeof dataset.defaultType === 'function') {
        return dataset.defaultType(tankersGroupType);
    }

    return dataset.defaultType;
};
